import React, { useMemo } from "react";
import {
  getWatermarkFontStack,
  measureWatermarkText,
  splitWatermarkScriptText,
  watermarkEffectStyle,
  watermarkFontSizePx,
  watermarkFontCssStyle,
  watermarkTextDecoration,
  watermarkTextAdvancedCss,
  watermarkTextBoxLayout,
  watermarkLogoDisplaySize,
  watermarkTilePositions,
  WATERMARK_CONTENT_TYPES,
} from "../Popup/watermarkSettings";
import {
  watermarkLogoMarkStyle,
  getWatermarkLogoCropRect,
} from "./watermarkImageFormatAdapter";
import WatermarkLogoMarkMedia from "../Popup/WatermarkLogoMarkMedia";
import "./WatermarkCanvasOverlay.css";

function WatermarkOverlayMarkText({ text, script = "normal" }) {
  const display = String(text || "");
  if (!display) return null;
  if (script !== "super" && script !== "sub") {
    return <span className="watermark-canvas-overlay__mark-text">{display}</span>;
  }
  const { base, affix } = splitWatermarkScriptText(display);
  if (!affix) {
    return <span className="watermark-canvas-overlay__mark-text">{display}</span>;
  }
  return (
    <span
      className={`watermark-canvas-overlay__mark-text watermark-canvas-overlay__mark-text--${script}`}
    >
      <span className="watermark-canvas-overlay__mark-base">{base}</span>
      <span
        className={`watermark-canvas-overlay__mark-affix watermark-canvas-overlay__mark-affix--${script}`}
      >
        {affix}
      </span>
    </span>
  );
}

/**
 * Live watermark marks on the editor canvas stage (pointer-events none).
 * Uses the same layout math as WatermarkPreview.
 */
const WatermarkCanvasOverlay = ({
  watermark = null,
  stageWidth = 0,
  stageHeight = 0,
}) => {
  const isLogoMode =
    watermark?.contentType === WATERMARK_CONTENT_TYPES.LOGO;
  const logoUrl = isLogoMode ? watermark?.logoUrl || null : null;
  const hasLogo = Boolean(logoUrl);
  const hasContent = isLogoMode
    ? hasLogo
    : Boolean(watermark?.text?.trim());

  const rotation = Number(watermark?.rotation) || 0;
  const offsetX = Number(watermark?.offsetX) || 0;
  const offsetY = Number(watermark?.offsetY) || 0;
  const fontFamily = getWatermarkFontStack(watermark?.fontId);

  const fontSizePx = watermarkFontSizePx(
    watermark?.size,
    stageWidth,
    stageHeight
  );

  const logoCropRect = getWatermarkLogoCropRect(watermark);

  const logoSize = useMemo(
    () =>
      watermarkLogoDisplaySize(
        watermark?.size,
        stageWidth,
        stageHeight,
        watermark?.logoNaturalWidth,
        watermark?.logoNaturalHeight,
        logoCropRect
      ),
    [
      watermark?.size,
      watermark?.logoNaturalWidth,
      watermark?.logoNaturalHeight,
      logoCropRect,
      stageWidth,
      stageHeight,
    ]
  );

  const textMetrics = useMemo(
    () =>
      measureWatermarkText(watermark?.text || "Your Text", fontSizePx, fontFamily, watermark?.fontWeight
      , {
        letterSpacing: watermark?.letterSpacing,
        textScript: watermark?.textScript,
      }),
    [
      watermark?.text,
      fontSizePx,
      fontFamily,
      watermark?.fontWeight,
      watermark?.letterSpacing,
      watermark?.textScript,
    ]
  );

  const markMetrics = isLogoMode
    ? { width: logoSize.width, height: logoSize.height }
    : textMetrics;

  const marks = useMemo(() => {
    if (!hasContent || !stageWidth || !stageHeight) return [];
    return watermarkTilePositions({
      tile: watermark.tile || "single",
      spacing: watermark.spacing,
      stageWidth,
      stageHeight,
      textWidth: markMetrics.width,
      textHeight: markMetrics.height,
      fontSizePx: isLogoMode
        ? Math.max(markMetrics.width, markMetrics.height)
        : fontSizePx,
      rotationDeg: rotation,
      position: watermark.position || "center",
      margin: watermark.margin,
      offsetX,
      offsetY,
      flushEdges: watermark.flushEdges !== false,
    });
  }, [
    hasContent,
    stageWidth,
    stageHeight,
    watermark,
    markMetrics,
    fontSizePx,
    rotation,
    offsetX,
    offsetY,
    isLogoMode,
  ]);

  const effectCss = isLogoMode
    ? {}
    : watermarkEffectStyle(
        watermark?.effect || "none",
        watermark?.color || "#FFFFFF"
      );

  const opacity =
    Math.max(0, Math.min(100, Number(watermark?.opacity) || 0)) / 100;

  if (!watermark?.enabled || !hasContent || marks.length === 0) {
    return null;
  }

  return (
    <div className="watermark-canvas-overlay" aria-hidden="true">
      {marks.map((pos, i) => {
        const key = `${pos.x.toFixed(4)}-${pos.y.toFixed(4)}-${i}`;
        const shared = {
          left: `${pos.x * 100}%`,
          top: `${pos.y * 100}%`,
          opacity,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        };
        if (isLogoMode) {
          const baseTransform = `translate(-50%, -50%) rotate(${rotation}deg)`;
          const { style: logoStyle, tint, imgStyle, effect, effectColor } =
            watermarkLogoMarkStyle(watermark, baseTransform);
          return (
            <span
              key={key}
              className="watermark-canvas-overlay__mark watermark-canvas-overlay__mark--logo"
              style={{
                ...shared,
                ...logoStyle,
                width: markMetrics.width,
                height: markMetrics.height,
              }}
            >
              <WatermarkLogoMarkMedia
                logoUrl={logoUrl}
                tint={tint}
                effect={effect}
                effectColor={effectColor}
                imgStyle={imgStyle}
                className="watermark-canvas-overlay__logo-img"
              />
            </span>
          );
        }
        const scriptMode = watermark?.textScript || "normal";
        return (
          <span
            key={key}
            className="watermark-canvas-overlay__mark"
            style={{
              ...shared,
              color: watermark?.color || "#FFFFFF",
              fontFamily,
              fontSize: fontSizePx,
              fontWeight: watermark?.fontWeight === "bold" ? 700 : 400,
              fontStyle: watermarkFontCssStyle(watermark?.fontStyle),
              textDecoration: watermarkTextDecoration(watermark),
              ...effectCss,
              ...watermarkTextAdvancedCss(watermark, fontSizePx),
              ...watermarkTextBoxLayout(watermark?.textAlign, markMetrics, {
                textScript: scriptMode,
              }),
            }}
          >
            <WatermarkOverlayMarkText text={watermark.text} script={scriptMode} />
          </span>
        );
      })}
    </div>
  );
};

export default WatermarkCanvasOverlay;
